import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { Toaster } from "sonner";

import "./styles.css";
import { App } from "./app/App";
import { AuthProvider } from "./app/AuthContext";
import { HttpError } from "@/lib/http";

// Restore theme (DaisyUI)
const savedTheme = "light"; // localStorage.getItem("theme");
if (savedTheme) {
  document.documentElement.setAttribute("data-theme", savedTheme);
}

// When any query or mutation surfaces a 401, redirect to /login immediately
// instead of waiting for RequireAuth's 30s background poll to notice.
// This also prevents a window where page-level cards show "Failed to load"
// after the session has already expired. 403 is intentionally NOT redirected —
// it means the user is authenticated but lacks permission; surface as-is.
function handleGlobalQueryError(err: unknown) {
  if (err instanceof HttpError && err.status === 401) {
    // Guard against redirect loops if we're already on /login.
    if (!window.location.pathname.endsWith("/login")) {
      window.location.href = "/login";
    }
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleGlobalQueryError }),
  mutationCache: new MutationCache({ onError: handleGlobalQueryError }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <AuthProvider>
          <App />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
